import { Badge } from '@/components/ui/badge';
import { User } from 'lucide-react';
import { CaseMilestone, MilestoneContact } from '@/hooks/useCaseMilestones';
import StageCard, { type StatusUpdateExtra } from './StageCard';

interface CasePersonStagesProps {
  personStagesByContact: Record<string, {
    contact: MilestoneContact | null;
    milestones: CaseMilestone[];
  }>;
  onToggleComplete: (milestone: CaseMilestone) => Promise<void>;
  onUpdateStatus?: (id: string, status: string, extra?: StatusUpdateExtra) => Promise<void>;
  isUpdating: boolean;
  isLocked?: boolean;
}

export default function CasePersonStages({ personStagesByContact, onToggleComplete, onUpdateStatus, isUpdating, isLocked = false }: CasePersonStagesProps) {
  const entries = Object.entries(personStagesByContact);
  if (entries.length === 0) return null;

  return (
    <div className="space-y-6">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
        Etapas por Pessoa
      </h3>

      {entries.map(([contactId, { contact, milestones }]) => {
        const name = contact
          ? `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sem nome'
          : 'Contato desconhecido';

        const completed = milestones.filter(m => m.status_category === 'completed').length;

        return (
          <div key={contactId} className="border border-border rounded-xl overflow-hidden">
            {/* Person header */}
            <div className="flex items-center justify-between px-4 py-3 bg-muted/30">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                  <User className="w-4 h-4 text-primary" />
                </div>
                <span className="font-medium text-sm text-foreground">{name}</span>
              </div>
              <Badge variant="outline" className="text-[11px]">
                {completed}/{milestones.length}
              </Badge>
            </div>

            {/* Person milestones */}
            <div className="p-3 space-y-2">
              {milestones.map((milestone, idx) => (
                <StageCard
                  key={milestone.id}
                  milestone={milestone}
                  index={idx}
                  onToggleComplete={onToggleComplete}
                  onUpdateStatus={onUpdateStatus}
                  isUpdating={isUpdating}
                  isLocked={isLocked}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
